import type { Chat } from "@rhema/client";
import { Button } from "@rhema/ui";
import { useState } from "react";
import { firstName } from "../data/view";
import { useApi, useClient } from "../session/session";
import { ReportSheet } from "./ReportSheet";
import { contactsChanged } from "./useContacts";

/** The other person in a direct chat that they started, when they are not in your contacts. */
export function unknownSender(chat: Chat, meId: string | undefined) {
  if (chat.kind !== "direct" || !meId || !chat.created_by || chat.created_by === meId) return null;
  const other = chat.members.find((m) => m.user.id !== meId)?.user;
  if (!other || other.id !== chat.created_by || other.is_contact || other.is_blocked) return null;
  return other;
}

/**
 * A bar above the composer when someone you do not know starts a chat with you, so you can block
 * them, report them or add them to your contacts.
 */
export function UnknownSenderBar({ chat, meId }: { chat: Chat; meId: string | undefined }) {
  const api = useApi();
  const client = useClient();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reporting, setReporting] = useState(false);
  const other = unknownSender(chat, meId);
  if (!other || done) return null;

  const run = async (job: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await job();
      setDone(true);
      contactsChanged();
      await client.refreshChat(chat.id);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      aria-label="Unknown sender"
      aria-busy={busy}
      className="flex flex-none flex-col gap-2 border-t border-line bg-surface-header px-4 py-3"
    >
      <p className="m-0 text-[15px] font-semibold">This person is not in your contacts</p>
      {error && (
        <p role="alert" className="m-0 text-sm text-scarlet">
          {error}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="danger"
          size="sm"
          disabled={busy}
          onClick={() => void run(() => api.block(other.id))}
        >
          Block
        </Button>
        <Button variant="outline" size="sm" disabled={busy} onClick={() => setReporting(true)}>
          Report
        </Button>
        <Button size="sm" disabled={busy} onClick={() => void run(() => api.addContact(other.id))}>
          Add to contacts
        </Button>
      </div>
      <ReportSheet
        open={reporting}
        onClose={(sent) => {
          setReporting(false);
          if (sent) void run(async () => undefined);
        }}
        target={{ userId: other.id, chatId: chat.id, name: firstName(other.display_name) }}
      />
    </section>
  );
}
